"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Check, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Corporate vs Weekend Leisure Volatility",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Delhi properties face stark weekday corporate demand versus weekend compression dips. We deploy dynamic stay-pattern pricing, corporate negotiated rate fencing, and weekend micro-stay packages to maintain consistent RevPAR.",
  metricLabel: "Strategy focus",
  metricValue: "Weekday-to-weekend yield balancing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive NCR Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Aerocity, Central Delhi, and transit corridors shift rates rapidly across MakeMyTrip, Booking.com, and Agoda. Automated rate alerts and algorithmic yield triggers capture high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "MICE, Weddings & OTA Parity Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent unauthorized B2B wholesaler leakage during high-density wedding and exhibition dates. We protect ADR during summit peaks and drive direct guest conversions through targeted booking engines.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct booking conversion boost"
}];
const seasonalityDetails = [{
  id: "peak",
  season: "Peak Season: October – March",
  subtitle: "High Corporate, MICE & Wedding Demand",
  status: "Pros & Revenue Advantages",
  points: ["Substantial ADR surges driven by trade expos, state delegations, and destination weddings.", "High corporate transient volume across Aerocity, Connaught Place, and South Delhi hubs.", "Opportunity for aggressive advance purchase rate fencing and non-refundable tiers."],
  isPositive: true
}, {
  id: "low",
  season: "Off-Peak / Shoulder: April – August",
  subtitle: "Intense Summer Heat & Diplomatic Recesses",
  status: "Cons & Market Challenges",
  points: ["Sharp dip in inbound leisure and international executive footfall during extreme summer months.", "Fierce price wars among 3-star and 4-star properties leading to unhedged margin erosion.", "Requires proactive base-load contracting, extended-stay packages, and MICE buffer pricing."],
  isPositive: false
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapedelhi" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Delhi
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Delhi presents a high-velocity capital ecosystem marked by corporate transit corridors, diplomatic summits, MICE exhibitions, and heavy seasonal wedding compression. Sunil Singh crafts data-backed revenue frameworks designed specifically for urban hotels, boutique stays, and NCR transit properties.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Pros / Cons Block */}
        <div className="mt-14 md:mt-20">
          <div className="text-center mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-secondary font-semibold">
              Seasonal Yield Dynamics
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mt-2">
              Delhi Seasonality: Strategic Opportunities & Market Pitfalls
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {seasonalityDetails.map((block, index) => <div key={block.id} data-index={index} className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {block.isPositive ? <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary/15 px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
                        <Check className="size-3.5 text-secondary" />
                        {block.status}
                      </span> : <span className="inline-flex items-center gap-1.5 rounded-md bg-destructive/15 px-2.5 py-1 text-xs font-semibold text-destructive">
                        <AlertTriangle className="size-3.5 text-destructive" />
                        {block.status}
                      </span>}
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-foreground font-serif mt-3">
                    {block.season}
                  </h4>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1 mb-5">
                    {block.subtitle}
                  </p>
                  <ul className="space-y-3">
                    {block.points.map((pt, pIndex) => <li key={pIndex} data-index={pIndex} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-1 size-1.5 rounded-full bg-secondary shrink-0" />
                        <span>{pt}</span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Delhi hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}